//
// Copyright (c) 2017 by Xolas.io. All Rights Reserved.
//

/*
Helper methods when posting stuff
----------------------------------------------------------------------- */
import axios from '@/lib/axiosClient'




export default {
  methods: {


    /**
     @param {String} url - the GET url.
     @param {Function} callback = null - if present will be called in success
     @param {String} text = 'information' - for messages.
     @param {Function} errorCallback = null - if present, will be called on failure of save.

     Calls fetchDataRawURL after wrapping getEndpoint(url)
     */
    async fetchData (url, callback = null, text = 'information', errorCallback = null) {
      return this.fetchDataRawURL(this.getEndpoint(url), callback, text, errorCallback)
    },



    /**
     @param {String} url - the GET raw url.
     @param {Function} callback = null - if present will be called in success
     @param {String} text = 'information' - for messages.
     @param {Function} errorCallback = null - if present, will be called on failure of save.
     */
    async fetchDataRawURL (url, callback = null, text = 'information', errorCallback = null) {

      axios.get(url)
        .then(response => {

          let data = response.data

          if (!this.checkWarning(response)) {
            if (response.data && response.data.data) {
              data = response.data.data
            }
          }

          if (this.isFunction(callback)) {
            callback(data)
          }

          return data

        })
        .catch(err => {
          // KAN-39: 401 is handled centrally in src/lib/axiosClient.js
          // (silent refresh + retry; redirect on terminal failure).
          console.error('fetchDataRawURL failed [', text, ']', url, 'message:', err)

          if (this.isFunction(errorCallback)) {
            errorCallback(err)
          }

          this.$notify(
            {
              title: 'Unable to fetch ' + text,
              message: this.describeFetchError(err),
              type: 'error',
              duration: 3000
            })
        })
    },


    /**
     XOL-440: a gate rejection is not a connectivity problem, but this handler
     rendered every failure as the raw axios Error ("Request failed with status
     code 400"), so a `tenant`/`role`/`route_acl` rejection reached the user as
     a network complaint. The API's gates all reply with a
     {error, reason, message} envelope alongside an X-Auth-Failure header —
     prefer the server's own wording when it sends one.

     @param {Error} err - the axios error.
     @returns {String|Error} a human-readable message, or the original error.
     */
    describeFetchError (err) {

      const body = err && err.response && err.response.data

      if (body && typeof body === 'object') {
        if (body.message) {
          return body.message
        }
        if (body.error) {
          return body.reason ? body.error + ': ' + body.reason : body.error
        }
      }

      return err
    },




    /**
     XOL-373 — fold the audit stamp out of a write response into the object the
     screen renders.

     The API stamps `updated_by` with the AUTHENTICATED contact (it no longer
     trusts the `updated_by` this mixin injects into `formData` below) and
     returns the saved record. Any screen showing "Last update: <date> by <name>"
     must therefore re-read both keys from the response, or it keeps displaying
     the PREVIOUS editor and the previous date until something refetches —
     which reads as though the edit never happened, while crediting someone else.

     It lives here, rather than in each component, because that is exactly how
     the gap arose: the same few lines were inlined into some success callbacks
     and absent from others, and nothing made the omission visible. Callers pass
     their own target because the object differs per screen (`details`, a row, a
     prop-shared licensee).

     A response that omits the keys leaves the existing values ALONE rather than
     blanking them — mirroring the API's own rule that a stale attribution is bad
     but an erased one is worse. `$set` because a target that never carried the
     keys would otherwise take a non-reactive assignment.

     @param {Object} target - the rendered object to update (e.g. `this.details`).
     @param {Object} data - the write response body.
     */
    applyAuditStamp (target, data) {
      if (!target || !data) return

      if (data.updated_at !== undefined) {
        this.$set(target, 'updated_at', data.updated_at)
      }

      if (data.updated_by !== undefined) {
        this.$set(target, 'updated_by', data.updated_by)
      }
    },

    /**
     @param {this} self - for messages and alerts.
     @param {Number} id - if null will create, else save.
     @param {String} url - the POST/PUT url.
     @param {Object} formData - data to be saved.
     @param {String} item for messages, etc.
     @param {Function} successCallback - if present will be called in success
     @param {Function} failCallback - if present, will be called on failure of save.
     @param {Boolean} showMessage = true, if false then no messages will be shown.
     @param {String/Array} tableRef refreshes table, or multiple tables, if array of string.
     @param {Boolean} notifyFailureCallback = false - forwarded to send() verbatim; see send()'s
       docblock for exactly which failure branches this opts failCallback into.
     @param {Boolean} allowViewOnly = false - forwarded to send() verbatim; bypasses the view-only
       write-block for self-service surfaces (e.g. own profile/password) that the API also exempts.

     Original saveForm, the tableRef param is a later addition, not break existing code.
     */
    async saveForm (self, url, id, formData, item = 'item', successCallback, failCallback, showMessage = true, tableRef = null, notifyFailureCallback = false, allowViewOnly = false) {

      if (!formData.updated_by) {
        formData.updated_by = this.userName()
      }

      if (id === null) {
        formData.created_by = this.userName()
      }

      var formAction = null

      // If has a valid ID for the object, the update, else add.
      if (id) {
        formAction = axios.put
        url = this.getEndpoint(url, id)
      } else {
        formAction = axios.post
        url = this.getEndpoint(url)
      }

      return this.send(self, url, formData, item, successCallback, failCallback, formAction, showMessage, 'Save', tableRef, notifyFailureCallback, allowViewOnly)
    },



    /**
     @param {this} self - for messages and alerts.
     @param {String} url - the POST/PUT url.
     @param {Object} formData - data to be saved.
     @param {String} item for messages, etc.
     @param {Function} successCallback - if present will be called in success
     @param {Function} failCallback - if present, will be called on failure of save.
     @param {axios} formAction - put, post, delete, etc.
     @param {Boolean} showMessage = true, if false then no messages will be shown.
     @param {String/Array} tableRef refreshes table, or multiple tables, if array of string.
     @param {Boolean} notifyFailureCallback = false - opt-in: when true, failCallback is also invoked
       on 418, 403, data.error, data.warning, the generic (has-response) fallback, and the no-response
       network-error/timeout/CORS branch (in addition to the always-on data.errors branch). Default
       false preserves original behaviour for all existing callers.

     Lower level function, for calls in PostMixins only.
     */
    async send (self, url, formData, item = 'item', successCallback = null, failCallback = null, formAction = axios.post, showMessage = false, actionType = 'Post', tableRef = null, notifyFailureCallback = false, allowViewOnly = false) {

      // XOL-371: view-only users are blocked from all writes EXCEPT their own
      // self-service surfaces (profile self-edit, own password), which pass
      // allowViewOnly=true. The API enforces the same exemption server-side
      // (PUT /api/profile is exempt from the view-only write-block).
      if (this.isViewOnly() && !allowViewOnly) {
        self.$message({
          type: 'warning',
          title: 'View Only User',
          message: 'You are view only, can\'t save'
        })

      // XOL-490/493: this used to be the only path in send() that returned
      // without invoking failCallback (see the removed XOL-487 comment this
      // replaces). Popup.vue:36 disables the trigger for view-only users, so
      // a keepOpenUntilSaved consumer never reaches this branch today — but
      // gating failCallback here the same way every sibling branch below
      // gates it means a future keepOpenUntilSaved consumer that doesn't
      // disable its own trigger no longer hangs "saving" forever.
      if (notifyFailureCallback && failCallback) { failCallback() }
      return

      }

      if (formAction) {
        formAction(url, formData)
        .then(response => {

          if (!this.checkWarning(response)) {

            let data = response.data

            if (response.data && response.data.length >= 1) {
              data = response.data[0]
            }

            this.eventTableRef(tableRef)

            if (successCallback && response && response.status === 200) {
              this.refreshStats()
              successCallback(data)
            }

            if (showMessage) {
              self.$message({
                type: 'success',
                title: 'Success',
                message: '[' + item + '] updated'
              })
            }

            return data

          }

          return null
        })
        .catch(err => {


          if (err.response) {
            console.error(actionType, err)

            let data = err.response.data

            // KAN-39: 401 is handled centrally in src/lib/axiosClient.js
            // (silent refresh + retry; redirect on terminal failure).

            // Status: 418 I'm a teapot - Used by Telco errors
            if (data && data.status && data.status === 418) {
              self.$notify({
                title: 'Problem',
                message: 'Unable to ' + actionType.toLowerCase() + ' ' + item + ' [' + err.response.data.error + ']',
                type: 'error',
                duration: 5500
              })

              if (notifyFailureCallback && failCallback) { failCallback(err) }
              return
            }

            // XOL-60: 403 Forbidden — backend role enforcement (see XOL-61)
            if (err.response.status === 403) {
              console.warn('[XOL-60] 403 on', actionType, item, '— user role likely mismatched for this action')
              self.$notify({
                title: 'Not allowed',
                message: (data && (data.error || data.message))
                  ? 'You do not have permission to ' + actionType.toLowerCase() + ' ' + item + ' [' + (data.error || data.message) + ']'
                  : 'You do not have permission to ' + actionType.toLowerCase() + ' ' + item + '. Contact your administrator if you believe this is a mistake.',
                type: 'error',
                duration: 5500
              })

              if (notifyFailureCallback && failCallback) { failCallback(err) }
              return
            }

            if (data && data.error) {
              self.$notify({
                title: 'Problem',
                message: data.error,
                type: 'error',
                duration: 5500
              })

              if (notifyFailureCallback && failCallback) { failCallback(err) }
              return
            }


            if (data && data.warning) {
              self.$notify({
                title: 'Warning',
                message: data.warning,
                type: 'warning',
                duration: 5500
              })

              if (notifyFailureCallback && failCallback) { failCallback(err) }
              return
            }


            if (data && data.errors) {

              let errors = data.errors
              let i = 0
              let len = 0


              // count props..
              for (const p in errors) {
                console.warn(`${p}`, errors[p][0])
                len++
              }


              for (const prop in errors) {

                self.$notify({
                  title: 'Problems [' + (i + 1) + ' of ' + len + ']',
                  message: errors[prop][0],
                  type: 'warning',
                  duration: 5500
                })

                i++

              }  // for

              if (failCallback) {
                failCallback(errors)
              }


            } else {

              self.$notify({
                title: 'Problems',
                message: 'Unable to ' + actionType.toLowerCase() + ' ' + item + ' [' + err.status + '] ' + (err.response.data ? err.response.data : err.response.message),
                type: 'error',
                duration: 5500
              })

              if (notifyFailureCallback && failCallback) { failCallback(err) }

            }

          } else {

            // XOL-487: no err.response means a network error / timeout / CORS
            // failure — the request never reached the server, or its reply
            // never reached us. Previously this branch didn't exist, so a
            // keepOpenUntilSaved consumer (RatesAction) wired up here would
            // never learn the save had failed and its drawer would hang
            // "saving" forever. The toast is unconditional, but failCallback
            // is gated on notifyFailureCallback — same opt-in as every other
            // branch above. A keepOpenUntilSaved consumer MUST pass
            // notifyFailureCallback = true, or its drawer will hang on a
            // network error; RatesAction does.
            self.$notify({
              title: 'Connection problem',
              message: 'Unable to reach the server. Your changes may not have been saved — check your connection and try again.',
              type: 'error',
              duration: 5500
            })

            if (notifyFailureCallback && failCallback) { failCallback(err) }

          } // if err.reponse

        })  // catch
      }
    },  // send




    /**
     @param {String/Array} tableRef refreshes table, or multiple tables, if array of string.

     Fires refresh for each tableRef.
     */
    eventTableRef (tableRef) {

      let tr = '-refresh-table'

      if (tableRef) {
        if (this.isString(tableRef)) {
          this.$events.emit(tableRef + tr)

        } else if (this.isArray(tableRef)) {
          for (var i = 0; i < tableRef.length; i++) {
            this.$events.emit(tableRef[i] + tr)
          }
        }
      }

    },




    /**
     @param {String} sUrl - download URL
     @param {String} fileName = null, for download

     @return {Boolean} true if successful, else false

     The downloadFile code is from: https://github.com/PixelsCommander/Download-File-JS
     (C) Copyright 2013 Denis Radin. Apache License terms.
      Modified to ES6 standards.
      */
    downloadFile (sUrl, fileName = null) {

      // iOS devices do not support downloading. We have to inform user about this.
      if (/(iP)/g.test(navigator.userAgent)) {
        alert('Your device does not support files downloading. Please try again in desktop browser.')
        return false
      }

      // If in Chrome or Safari - download via virtual link click
      if (this.getStore().isChrome || this.getStore().isSafari) {
        // Creating new link node.
        var link = document.createElement('a')

        link.target = '_blank'
        link.href = sUrl

        if (link.download !== undefined) {
          // Set HTML5 download attribute. This will prevent file from opening if supported.
          fileName = fileName ? fileName : sUrl.substring(sUrl.lastIndexOf('/') + 1, sUrl.length)
          link.download = fileName
        }

        // Dispatching click event.
        if (document.createEvent) {
          var e = document.createEvent('MouseEvents')
          e.initEvent('click', true, true)
          link.dispatchEvent(e)
          return true
        }
      }

      // Force file download (whether supported by server).
      if (sUrl.indexOf('?') === -1) {
        sUrl += '?download'
      }

      window.open(sUrl, '_self')
      return true
    },



    /**
     @param {Object} data - file data to download.
     @param {String} fileName for messages, etc.
     @param {String} type default: 'application/octet-stream'

     download data via dynamic link.
     */
    download (data, filename, type = 'application/octet-stream') {
      var file = new Blob([data], {type: type})
      if (window.navigator.msSaveOrOpenBlob) {
        window.navigator.msSaveOrOpenBlob(file, filename)  // IE10+
      } else { // Others
        var a = document.createElement('a'),
          url = URL.createObjectURL(file)
        a.href = url
        a.download = filename
        document.body.appendChild(a)
        a.click()
        setTimeout(function () {
          document.body.removeChild(a)
          window.URL.revokeObjectURL(url)
        }, 0)
      }
    },




    /**

     @param {this} self - for messages and alerts.
     @param {Number} id - the ID of the record to delete.
     @param {String} url - the POST/PUT url.
     @param {String} message - Message to display in the confirm dialog.
     @param {Function} callback - if present will be called with what ever the server responds with.
     @param {String/Array} tableRef refreshes table, or multiple tables, if array of string.

     If confirm = true pops up a confirm dialog with contents for 'message'
     */
    async deleteItem (self, id, url, message, callback, confirm = true, tableRef = null, confirmYesText = 'Yes') {

      if (this.isViewOnly()) {
        self.$message({
          type: 'warning',
          title: 'View Only User',
          message: 'You are view only, can\'t delete'
        })

      return

      }


      url = this.getEndpoint(url, id)

      if (confirm) {
        self.$confirm('This will permanently delete <b>' + message + '</b> Continue?', 'Warning', {
          confirmButtonText: confirmYesText,
          cancelButtonText: 'No',
          center: true,
          dangerouslyUseHTMLString: true,
          type: 'error'
        }).then(() => {
          return this.deleteRaw(self, url, id, message, callback, true, tableRef)
        }).catch(() => {
          self.$message({
            type: 'info',
            message: 'Delete cancelled'
          })
        })
      } else {
        return this.deleteRaw(self, url, id, message, callback, false, tableRef)
      }

      return null
    },   // deleteItem



    /**

     @param {this} self - for messages and alerts.
     @param {Number} id - the ID of the record to delete.
     @param {String} url - the POST/PUT url.
     @param {String} message - Message to display in the confirm dialog.
     @param {Function} callback - if present will be called with what ever the server responds with.
     @param {String/Array} tableRef refreshes table, or multiple tables, if array of string.

     Without wrapping getEndpoint(url).
     */
    async deleteRaw (self, url, id, message, callback, showMsgs = true, tableRef = null) {
      axios.delete(url, id)
      .then(response => {
        if (!this.checkWarning(response, showMsgs)) {

          this.refreshStats()

          if (showMsgs) {
            self.$message({type: 'success', message: 'Delete [' + message + '] completed'})
          }

          let data = response.data[0]

          this.eventTableRef(tableRef)

          if (callback) {
            callback(data)
          }

          return data
        }

      })
      .catch(err => {
        self.$notify({
          type: 'error',
          message: 'Delete failed ' + err.message
        })
      })  // catch
    },    // deleteRaw




    /**

     @param {this} self - for messages and alerts.
     @param {String} url - the POST/PUT url.
     @param {String} message - Message to display in the confirm dialog.
     @param {Function} callback - if present will be called with what ever the server responds with.

     Without ID, your URL must contain valid delete clauses.
     */
    async delete (self, url, message, callback) {
      url = this.getEndpoint(url)

      self.$confirm('This will permanently delete <b>' + message + '</b> Continue?', 'Warning', {
        confirmButtonText: 'Yes',
        cancelButtonText: 'No',
        center: true,
        dangerouslyUseHTMLString: true,
        type: 'warning'
      }).then(() => {

        axios.delete(url)
        .then(response => {

          if (callback) {
            callback(response)
          }

          if (!this.checkWarning(response)) {

            this.refreshStats()

            self.$message({
              type: 'success',
              message: 'Delete [' + message + '] completed'
            })

          }

          return response
        })
        .catch(err => {
          self.$notify({
            type: 'error',
            message: 'Delete failed ' + err.message
          })
        })  // catch
      }).catch(() => {
        self.$message({
          type: 'info',
          message: 'Delete cancelled'
        })
      })
    }   // delete

  }
}
